export const PICKER_DESKTOP_WIDTH = 520;
export const PICKER_MAX_HEIGHT = 560;
export const VIEWPORT_PADDING = 12;
export const PICKER_GAP = 8;
export const BELOW_THRESHOLD_EXTRA = 16;

/**
 * Effective bottom of the usable viewport (avoids workflow AI input bar when present).
 */
export function getEffectiveViewportBottom(windowHeight = window.innerHeight) {
  if (typeof document === 'undefined') {
    return windowHeight - VIEWPORT_PADDING;
  }

  const aiInput = document.querySelector('[data-testid="workflow-ai-input"]');
  if (aiInput) {
    const rect = aiInput.getBoundingClientRect();
    if (rect.top > 0 && rect.top < windowHeight) {
      return Math.max(VIEWPORT_PADDING, rect.top - PICKER_GAP);
    }
  }

  const bottomBar = document.querySelector('[data-workflow-bottom-bar]');
  if (bottomBar) {
    const rect = bottomBar.getBoundingClientRect();
    if (rect.top > 0 && rect.top < windowHeight) {
      return Math.max(VIEWPORT_PADDING, rect.top - PICKER_GAP);
    }
  }

  return windowHeight - VIEWPORT_PADDING;
}

export function getPickerWidth(viewportWidth = window.innerWidth) {
  const maxWidth = viewportWidth - VIEWPORT_PADDING * 2;
  if (viewportWidth < 640) {
    return maxWidth;
  }
  return Math.min(PICKER_DESKTOP_WIDTH, maxWidth);
}

/**
 * Compute fixed picker placement relative to an anchor button/input.
 */
export function computePickerPosition(anchorRect, options = {}) {
  const viewportWidth = options.viewportWidth ?? window.innerWidth;
  const viewportTop = options.viewportTop ?? VIEWPORT_PADDING;
  const viewportBottom =
    options.viewportBottom ?? getEffectiveViewportBottom(options.viewportHeight ?? window.innerHeight);
  const gap = options.gap ?? PICKER_GAP;
  const preferredMaxHeight = options.preferredMaxHeight ?? PICKER_MAX_HEIGHT;
  const width = options.width ?? getPickerWidth(viewportWidth);

  const spaceBelow = viewportBottom - anchorRect.bottom - gap;
  const spaceAbove = anchorRect.top - viewportTop - gap;

  const openAbove =
    spaceBelow < preferredMaxHeight + BELOW_THRESHOLD_EXTRA && spaceAbove >= spaceBelow;

  let maxHeight;
  let top;

  if (openAbove) {
    maxHeight = Math.min(preferredMaxHeight, Math.max(0, spaceAbove - BELOW_THRESHOLD_EXTRA));
    top = anchorRect.top - gap - maxHeight;
    if (top < viewportTop) {
      maxHeight = Math.max(120, anchorRect.top - gap - viewportTop);
      top = viewportTop;
    }
  } else {
    maxHeight = Math.min(preferredMaxHeight, Math.max(0, spaceBelow - BELOW_THRESHOLD_EXTRA));
    top = anchorRect.bottom + gap;
    if (top + maxHeight > viewportBottom) {
      maxHeight = Math.max(120, viewportBottom - top);
    }
  }

  maxHeight = Math.max(120, maxHeight);

  let left = anchorRect.left;
  if (left + width > viewportWidth - VIEWPORT_PADDING) {
    left = viewportWidth - VIEWPORT_PADDING - width;
  }
  left = Math.max(VIEWPORT_PADDING, left);

  return {
    top,
    left,
    width,
    maxHeight,
    placement: openAbove ? 'above' : 'below',
  };
}
